import { useSelector } from "react-redux";
import { Input, Button, Divider } from "semantic-ui-react";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { getCampaign, getCampaignByDeliveryCode } from "services/apiMiddleware";
import { useTranslation } from "react-i18next";

const ErrorText = ({ name }) => <p className="form-error-text">{<ErrorMessage name={name} />}</p>;

function DeliveryInit(props) {
  const { onCampaign, onNoCampaign, onHasDeliveries } = props;
  const { t } = useTranslation("common");

  return (
    <div className="delivery-init">
      <p>{t("delivery_code_form_information")}</p>

      <DeliveryCodeForm onSuccess={onCampaign} onHasDeliveries={onHasDeliveries} t={t} />

      <Divider horizontal>{t("or")}</Divider>

      <Button
        basic
        type="button"
        onClick={onNoCampaign}
        data-test="delivery_code_form_no_delivery_code_button"
        className="no-delivery-code-button"
      >
        {t("delivery_code_form_no_delivery_code")}
      </Button>
    </div>
  );
}

const DELIVERY_CODE_MAX_CHARACTERS = 100;

const DeliveryCodeForm = ({ onSuccess, onHasDeliveries, t }) => {
  const { username } = useSelector((state) => state.activeUser.item);

  const deliveryCodeSchema = yup.object().shape({
    deliveryCode: yup
      .string()
      .required(t("delivery_code_form_error_empty"))
      .max(
        DELIVERY_CODE_MAX_CHARACTERS,
        t("delivery_code_form_error_character_length", {
          max_characters: DELIVERY_CODE_MAX_CHARACTERS,
        })
      ),
  });

  const submit = async (values, actions) => {
    try {
      const { data, errorMessage } = await getCampaignByDeliveryCode(values.deliveryCode, username);

      if (errorMessage) {
        actions.setFieldError("deliveryCode", errorMessage);
      } else {
        // Get the campaign and check if user has access to any of its assets:
        const campaign = (await getCampaign(data.id)).data;

        // User has deliveries in campaign -> redirect to campaign view
        if (campaign && campaign.assets.items.length > 0) onHasDeliveries(campaign);
        // No deliveries -> continue to creating assets
        else onSuccess(data);
      }
    } catch (error) {
      console.error(error);
      actions.setFieldError("deliveryCode", t("something_went_wrong"));
    }
  };

  return (
    <Formik
      validationSchema={deliveryCodeSchema}
      initialValues={{ deliveryCode: "" }}
      onSubmit={submit}
    >
      {({ values, setFieldValue, errors, isSubmitting }) => (
        <Form>
          <Input
            placeholder={t("delivery_code_form_input_placeholder")}
            data-test="delivery_code_form_input"
            name="deliveryCode"
            value={values.deliveryCode}
            onChange={(_e, { value, name }) => setFieldValue(name, value.trim())}
            error={errors.deliveryCode !== undefined}
            fluid
          />

          <ErrorText name="deliveryCode" />

          <Button
            primary
            fluid
            type="submit"
            loading={isSubmitting}
            data-test="delivery_code_search_button"
            style={{ marginTop: "1rem" }}
          >
            {t("delivery_code_form_button_search")}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default DeliveryInit;
